import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import { Bold, Italic, List, Link as LinkIcon } from "lucide-react";

export interface RichEditorHandle { getHtml(): string; setHtml(html: string): void; focus(): void; insertText(t: string): void }

export const RichEditor = forwardRef<RichEditorHandle, { placeholder?: string; initialHtml?: string; minHeight?: number; onChange?: (html: string) => void; autoFocus?: boolean; className?: string }>(
  function RichEditor({ placeholder = "Write your message", initialHtml = "", minHeight = 90, onChange, autoFocus, className = "" }, ref) {
    const el = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
      getHtml: () => el.current?.innerHTML ?? "",
      setHtml: (html) => { if (el.current) { el.current.innerHTML = html; onChange?.(html); } },
      focus: () => el.current?.focus(),
      insertText: (t) => { el.current?.focus(); document.execCommand("insertText", false, t); },
    }));
    useEffect(() => { if (el.current && initialHtml && !el.current.innerHTML) el.current.innerHTML = initialHtml; if (autoFocus) el.current?.focus(); }, [initialHtml, autoFocus]);
    const cmd = (c: string, v?: string) => { el.current?.focus(); document.execCommand(c, false, v); onChange?.(el.current?.innerHTML ?? ""); };
    return (
      <div className={className}>
        <div
          ref={el}
          className="editor"
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          style={{ minHeight }}
          onInput={() => onChange?.(el.current?.innerHTML ?? "")}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") { e.preventDefault(); cmd("bold"); }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") { e.preventDefault(); cmd("italic"); }
          }}
          onPaste={(e) => {
            // Paste as plain text to keep outbound HTML clean.
            e.preventDefault();
            const t = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, t);
          }}
        />
        <div className="row gap-2" style={{ padding: "0 10px 6px" }}>
          <button type="button" className="btn ghost sm icon" title="Bold (⌘B)" onMouseDown={(e) => { e.preventDefault(); cmd("bold"); }}><Bold size={13} /></button>
          <button type="button" className="btn ghost sm icon" title="Italic (⌘I)" onMouseDown={(e) => { e.preventDefault(); cmd("italic"); }}><Italic size={13} /></button>
          <button type="button" className="btn ghost sm icon" title="Bulleted list" onMouseDown={(e) => { e.preventDefault(); cmd("insertUnorderedList"); }}><List size={13} /></button>
          <button type="button" className="btn ghost sm icon" title="Link" onMouseDown={(e) => { e.preventDefault(); const u = prompt("Link URL"); if (u) cmd("createLink", u); }}><LinkIcon size={13} /></button>
        </div>
      </div>
    );
  },
);
